import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { ChevronDown } from 'react-feather';
import { Dropdown } from '@/shared/components/Dropdown';
import type { MenuItemProps } from '@/shared/components/Menu/types';
import {
  listEquipment,
  type EquipmentListItem,
} from '@/epics/facilityManagement/api/listEquipmentApiService';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { FILL_TASK_FORM_LABELS as L } from '../../constants/labels';

const PAGE_LIMIT = 10;

export interface EquipmentDropdownProps {
  /** Internal UUID (clients.id) — passed to listEquipment. */
  clientId: string;
  /** Internal UUID (facilities.id) — passed to listEquipment. */
  facilityId: string;
  /** Currently selected equipment UUID (equipments.id). Empty string = none. */
  selectedId: string;
  /**
   * Display name of the already-selected item.
   * Shown immediately on render so the label doesn't blank out while the
   * equipment list re-fetches (e.g. when the user navigates back to Step 1).
   */
  selectedName?: string;
  /** Fires with the full equipment row so the parent can populate detail fields. */
  onSelect: (item: EquipmentListItem) => void;
  /** Floating label text rendered inside the trigger field. */
  label: ReactNode;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string | undefined;
}

const EquipmentDropdownComponent = function EquipmentDropdown({
  clientId,
  facilityId,
  selectedId,
  selectedName,
  onSelect,
  label,
  disabled = false,
  error = false,
  errorMessage,
}: EquipmentDropdownProps) {
  const [items, setItems] = useState<EquipmentListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);

  // Synchronous guard — prevents concurrent fetches when onScrollEnd fires rapidly
  // before the loading state re-render lands.
  const loadingRef = useRef(false);

  // Incremented on each debouncedSearch change; fetchMore discards in-flight
  // results from the previous search session to prevent stale page appends.
  const fetchGenRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    fetchGenRef.current += 1;
    const gen = fetchGenRef.current;
    setItems([]);
    setPage(1);
    setHasMore(true);
    setFetchError('');

    const fetchFirstPage = async () => {
      loadingRef.current = true;
      setLoading(true);
      const result = await listEquipment({
        clientId,
        facilityId,
        page: 1,
        limit: PAGE_LIMIT,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (!mounted || fetchGenRef.current !== gen) {
        loadingRef.current = false;
        return;
      }
      if (result.success && result.data) {
        setItems(result.data.equipment);
        setHasMore(result.data.pagination.hasNextPage);
        setPage(1);
      } else {
        setFetchError(result.error ?? L.EQUIPMENT_LOAD_ERROR);
        setHasMore(false);
      }
      setLoading(false);
      loadingRef.current = false;
    };
    void fetchFirstPage();
    return () => {
      mounted = false;
    };
  }, [clientId, facilityId, debouncedSearch]);

  const fetchMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) {
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    const gen = fetchGenRef.current;
    const nextPage = page + 1;
    const result = await listEquipment({
      clientId,
      facilityId,
      page: nextPage,
      limit: PAGE_LIMIT,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    });
    if (fetchGenRef.current !== gen) {
      loadingRef.current = false;
      return;
    }
    if (result.success && result.data) {
      setItems((prev) => {
        const incoming = result.data?.equipment ?? [];
        const ids = new Set(prev.map((e) => e.id));
        return [...prev, ...incoming.filter((e) => !ids.has(e.id))];
      });
      setHasMore(result.data.pagination.hasNextPage);
      setPage(nextPage);
    } else {
      setHasMore(false);
      setFetchError(result.error ?? L.EQUIPMENT_LOAD_ERROR);
    }
    setLoading(false);
    loadingRef.current = false;
  }, [hasMore, page, clientId, facilityId, debouncedSearch]);

  const menuItems: MenuItemProps[] = items.map((e) => ({
    id: e.id,
    label: e.name,
    toolTip: false as const,
  }));

  if (loading) {
    menuItems.push({
      id: '__loading__',
      label: L.EQUIPMENT_LOADING,
      disabled: true,
    });
  } else if (fetchError && items.length === 0) {
    menuItems.push({ id: '__error__', label: fetchError, disabled: true });
  } else if (!loading && items.length === 0) {
    menuItems.push({
      id: '__empty__',
      label: L.EQUIPMENT_NO_RESULTS,
      disabled: true,
    });
  }

  const selected = items.find((e) => e.id === selectedId) ?? null;
  // Use the pre-loaded name until the API list arrives so the trigger never
  // blanks out when the user navigates back to this step.
  const displayName = selected?.name ?? (selectedId ? selectedName : undefined);

  return (
    <Dropdown
      menuFullWidth
      toolTip={false}
      showSearch
      searchPlaceholder="Szukaj urządzenia"
      menuMaxVisibleItems={5}
      autoMaxHeight
      items={menuItems}
      {...(selectedId ? { selectedId } : {})}
      disabled={disabled}
      error={error}
      {...(errorMessage ? { errorMessage } : {})}
      onSearchChange={setSearchQuery}
      onItemClick={(id) => {
        if (id !== '__loading__' && id !== '__empty__' && id !== '__error__') {
          const eq = items.find((e) => e.id === id);
          if (eq) {
            onSelect(eq);
          }
        }
      }}
      onScrollEnd={() => {
        if (hasMore && !loadingRef.current) {
          void fetchMore();
        }
      }}
      trigger={({ open, toggle, disabled: triggerDisabled }) => {
        const labelFloating = open || !!displayName;
        const labelColor = open ? '#2a4455' : '#6a7278';
        return (
          <button
            type="button"
            onClick={triggerDisabled ? undefined : toggle}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-disabled={triggerDisabled}
            data-testid="equipment-dropdown-trigger"
            className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
              triggerDisabled
                ? 'cursor-not-allowed text-neutral-60'
                : 'cursor-pointer text-neutral-10'
            }`}
          >
            <div
              className="flex-1 min-w-0 relative flex flex-col justify-center"
              style={{ minHeight: 40 }}
            >
              <span
                className="absolute left-0 pointer-events-none origin-top-left font-normal"
                style={{
                  color: labelColor,
                  top: labelFloating ? 0 : 10,
                  fontSize: labelFloating ? 12 : 16,
                  lineHeight: labelFloating ? '16px' : '20px',
                  transition:
                    'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                }}
              >
                {label}
              </span>
              {displayName && (
                <span
                  className="text-neutral-10 block w-full truncate"
                  style={{
                    marginTop: 16,
                    fontSize: 14,
                    lineHeight: '20px',
                    fontWeight: 400,
                  }}
                >
                  {displayName}
                </span>
              )}
            </div>
            <ChevronDown
              strokeWidth={1.5}
              className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          </button>
        );
      }}
    />
  );
};

export const EquipmentDropdown = React.memo(EquipmentDropdownComponent);
